import React, { useState, useEffect, useMemo } from "react";
import ovensData from "./assets/ovens.json";

const attributeLabels = {
  supplier: "Supplier",
  model: "Model Number",
  altitude: "Altitude",
  roastingWings: "Roasting Wings",
  stickers: "Stickers"
};

const attributeKeys = ovensData.length > 0 ? Object.keys(ovensData[0]) : [];

const OvenDropdowns = () => {
  const [selectedValues, setSelectedValues] = useState({});
  const [options, setOptions] = useState({});

  useEffect(() => {
    if (attributeKeys.length > 0) {
      const firstAttr = attributeKeys[0];
      const uniqueFirst = [...new Set(ovensData.map(item => item[firstAttr]))];
      setOptions(prev => ({ ...prev, [firstAttr]: uniqueFirst }));
    }
  }, []);

  useEffect(() => {
    let filtered = ovensData;
    const newOptions = {};
    for (let i = 0; i < attributeKeys.length; i++) {
      const attr = attributeKeys[i];
      if (i > 0) {
        for (let j = 0; j < i; j++) {
          const prevAttr = attributeKeys[j];
          if (selectedValues[prevAttr]) {
            filtered = filtered.filter(item => item[prevAttr] === selectedValues[prevAttr]);
          }
        }
      }
      newOptions[attr] = [...new Set(filtered.map(item => item[attr]))];
    }
    setOptions(newOptions);
  }, [selectedValues]);

  const handleChange = (attr, value) => {
    const idx = attributeKeys.indexOf(attr);
    const newSelected = { ...selectedValues };
    newSelected[attr] = value;
    for (let i = idx + 1; i < attributeKeys.length; i++) {
      newSelected[attributeKeys[i]] = "";
    }
    setSelectedValues(newSelected);
  };

  // Find the unique roastingWings values for the current dropdown selections
  const filteredRows = ovensData.filter(item =>
    attributeKeys.slice(0, 3).every(attr => selectedValues[attr] === item[attr])
  );
  const uniqueRoastingWings = useMemo(() => [...new Set(filteredRows.map(row => row.roastingWings))], [filteredRows]);
  const onlyOneRoastingWings = uniqueRoastingWings.length === 1;
  const roastingWingsValue = onlyOneRoastingWings ? uniqueRoastingWings[0] : !!selectedValues['roastingWings'];

  // When the first three dropdowns are selected, show the roastingWings checkbox
  // If only one value, reflect and disable; if multiple, allow user to choose
  const handleRoastingWingsChange = (e) => {
    setSelectedValues(prev => ({ ...prev, roastingWings: e.target.checked }));
  };

  // Assign stickers value in the background when all required fields are selected
  useEffect(() => {
    if (
      attributeKeys.slice(0, 3).every(attr => selectedValues[attr]) &&
      selectedValues['roastingWings'] !== undefined
    ) {
      const filtered = ovensData.filter(item =>
        attributeKeys.slice(0, 3).every(attr => selectedValues[attr] === item[attr]) &&
        item['roastingWings'] === (onlyOneRoastingWings ? uniqueRoastingWings[0] : !!selectedValues['roastingWings'])
      );
      if (filtered.length > 0) {
        const stickers = filtered[0].stickers;
        if (stickers && selectedValues['stickers'] !== stickers) {
          setSelectedValues(prev => ({ ...prev, stickers }));
        }
      }
    }
  }, [selectedValues, onlyOneRoastingWings, uniqueRoastingWings]);

  // Append selected stickers value to downloadUrl if present
  const stickersValue = selectedValues['stickers'];
  console.log('DEBUG: stickersValue =', stickersValue); // Debug print
  const downloadUrl = stickersValue
    ? `https://hutlinkcontent.yum.com/documents/d/hutlink/${encodeURIComponent(stickersValue)}`
    : "https://hutlinkcontent.yum.com/documents/d/hutlink/";

  // UI styles
  const labelStyle = {
    color: '#e89c5b',
    fontWeight: 'bold',
    fontSize: '1.1em',
    width: '180px',
    display: 'inline-block',
    textAlign: 'right',
    marginRight: '16px',
    verticalAlign: 'middle'
  };
  const selectStyle = {
    width: '70%',
    padding: '8px',
    fontSize: '1em',
    marginBottom: '8px',
    borderRadius: '6px',
    border: '1px solid #cfd8dc',
    background: '#f8f8f8'
  };
  const dividerStyle = {
    border: 'none',
    borderTop: '2px solid #f2f2f2',
    margin: '24px 0 16px 0'
  };
  const instructionStyle = {
    color: '#252523',
    fontSize: '1em',
    margin: '0px 0px 0px 192px',
  };
  const buttonStyle = {
    background: '#e53935',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    padding: '12px 32px',
    fontWeight: 'bold',
    fontSize: '1.1em',
    marginTop: '24px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  };
  const helpBoxStyle = {
    background: 'white',
    borderRadius: '4px',
    margin: '32px auto 0 auto',
    padding: '18px 32px',
    maxWidth: '700px',
    boxShadow: '0 2px 8px rgb(0 0 0 / 52%)'
  };
  const helpIconStyle = {
    fontWeight: 'bold',
    marginRight: '8px',
    fontSize: '1.2em'
  };
  const helpTextStyle = {
    color: '#222',
    fontSize: '1em',
    marginTop: '8px'
  };
  const emailStyle = {
    color: '#3b82f6',
    textDecoration: 'none'
  };

  return (
    <div className={"container white-bordered-panel"}>
      <h1 style={{ fontFamily: 'inherit', fontWeight: 600, marginBottom: '8px' }}>Printable Oven Settings</h1>
      <div style={{ color: '#444', fontSize: '1.1em', marginBottom: '16px' }}>
        Select your oven information below. Print one sheet for each oven deck (you can print multiple copies if desired)
      </div>
      <hr style={dividerStyle} />
      <form style={{ marginBottom: '0' }}>
        {attributeKeys.slice(0, 3).map(attr => (
          <div key={attr} style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
            <span style={labelStyle}>{attributeLabels[attr] || attr}</span>
            <select
              style={selectStyle}
              value={selectedValues[attr] || ""}
              onChange={e => handleChange(attr, e.target.value)}
              disabled={attr !== attributeKeys[0] && !selectedValues[attributeKeys[attributeKeys.indexOf(attr) - 1]]}
            >
              <option value="">Select {attributeLabels[attr] || attr}</option>
              {options[attr] && options[attr].map(opt => (
                <option key={opt} value={opt}>{String(opt)}</option>
              ))}
            </select>
          </div>
        ))}
        {/* Roasting Wings checkbox */}
        {attributeKeys.slice(0, 3).every(attr => selectedValues[attr]) && filteredRows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
            <span style={labelStyle}>{attributeLabels['roastingWings']}</span>
            <input
              type="checkbox"
              checked={!!roastingWingsValue}
              onChange={handleRoastingWingsChange}
              disabled={onlyOneRoastingWings}
            />
            <span style={{ marginLeft: '8px' }}>{!!roastingWingsValue ? 'Yes' : 'No'}</span>
          </div>
        )}
        {/* Stickers dropdown filtered by all four fields (hidden, assignment is in background) */}
        {/*
        {attributeKeys.slice(0, 3).every(attr => selectedValues[attr]) && selectedValues['roastingWings'] !== undefined && filteredStickers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
            <span style={labelStyle}>{attributeLabels['stickers']}</span>
            <select
              style={selectStyle}
              value={selectedValues['stickers'] || ""}
              onChange={e => handleChange('stickers', e.target.value)}
            >
              <option value="">Select {attributeLabels['stickers']}</option>
              {filteredStickers.map(opt => (
                <option key={opt} value={opt}>{String(opt)}</option>
              ))}
            </select>
          </div>
        )}
        */}
      </form>
      <div style={instructionStyle}>Make selections for your Oven Settings Sheet</div>
      <div style={{ textAlign: 'center' }}>
        {attributeKeys.slice(0, 3).every(attr => selectedValues[attr]) && selectedValues['roastingWings'] !== undefined && selectedValues['stickers'] && (
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
            <button type="button" style={buttonStyle}>
              <span role="img" aria-label="download" style={{ marginRight: '8px' }}>⬇️</span> Download Your Oven Settings Sheet
            </button>
          </a>
        )}
      </div>
      <div style={helpBoxStyle}>
        <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '1.1em', marginBottom: '6px' }}>
          <span style={helpIconStyle}>⚡</span> Need Help?
        </div>
        <div style={helpTextStyle}>
          If you have questions, please contact <a href="mailto:EngineeringOvens@pizzahut.com" style={emailStyle}>EngineeringOvens@pizzahut.com</a>.
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '18px' }}>
        <img src="/pizza-hut-sauce.png" alt="Pizza Hut Logo" style={{ width: '48px', marginBottom: '4px' }} />
        <div style={{ color: '#888', fontWeight: 'bold', fontSize: '1.1em' }}>0.3.5</div>
      </div>
    </div>
  );
};

export default OvenDropdowns;
